import {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
  JSX,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { PATHS } from 'src/common/constants';
import HeadingWithDetails from '../../components/HeadingWithDetails';
import WithNavigation from '../../layouts/WithNavigation';
import moment from 'moment';
import Loader from '../../components/Loader';
import Overlay from '../../components/Overlay';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { upload } from 'src/helpers/upLoadHelper';
import { selectPropertyWalkThroughFormIsVisible } from 'src/store/selectors/propertyWalkThroughForm';
import PropertyWalkThroughForm from '../../components/PropertyWalkThroughForm';
import KeyHandoffComponent from './KeyHandoffComponent';
import { selectSuiteFormData } from 'src/store/selectors/suiteForm';
import {
  setSuiteFormData,
  clearSuiteFormData,
} from 'src/store/actions/suiteForm';
import MoreDetails from './MoreDetails';
import { serviceRequestTableLocalTime } from 'src/helpers/validationHelper';
import { store } from 'src/store';
import { BrandingContext } from 'src/branding/BrandingContextProvider';
import { Headline } from './header/header';
import SuiteTabs from 'src/pages/Suite/Bottom/SuiteTabs';
import SuiteActions from 'src/pages/Suite/Actions/Actions';
import SuiteBreadcrumb from 'src/pages/Suite/Breadcrumb/Breadcrumb';
import { SuiteDetailsComponents } from 'src/pages/Suite/Components/SuiteDetailsComponents';
import { toast } from 'react-toastify';
import {
  CalendlyData,
  useSuiteActions,
  UseSuiteActionsReturn,
} from 'src/SharedCustomHooks/UseSuiteActions/useSuiteActions';
import {
  useKeyHandoffSubmission,
  UseKeyHandoffSubmissionReturn,
} from 'src/SharedCustomHooks/UseSuiteActions/useKeyHandoffSubmission';
import {
  WalkThrough,
  NoteDocument,
  Property,
  Suite as SuiteType,
  Asset,
  Counts,
  Contact,
  UserRole,
} from 'src/SharedCustomHooks/UseSuiteActions/types';
import { ModulePermission } from 'src/components/ContactForm/types';

interface SuiteProps {
  match: {
    params: {
      id: string;
    };
  };
  location: {
    state?: {
      data?: {
        serviceRequestId?: string;
      };
      property?: Property;
      suite?: SuiteType;
      activeTab?: number;
      from?: string;
      onNavigationBack?: boolean;
    };
  };
  history: any;
}

export interface SuiteState {
  showOverlay: boolean;
  isEventShow: boolean;
  showOverlayKeyHandoff: boolean;
  suite: SuiteType | null;
  feedBackReportPopup: boolean;
  counts: Counts | null;
  walkThroughs: WalkThrough[];
  properties: Property[];
  property: Property | null;
  propertyManagerDetails: Contact | null;
  allowedTabList: ModulePermission[];
  notesList: NoteDocument[];
  moreDetails: any | null;
  userRoles: UserRole[];
  assets: Asset[];
}

const Suite = ({ match, location, history }: SuiteProps) => {
  const { branding } = useContext(BrandingContext);
  const dispatch = useDispatch();

  const [state, setState] = useState<SuiteState>({
    showOverlay: false,
    isEventShow: false,
    showOverlayKeyHandoff: false,
    suite: null,
    feedBackReportPopup: false,
    counts: null,
    walkThroughs: [],
    properties: [],
    property: null,
    propertyManagerDetails: null,
    allowedTabList: [],
    notesList: [],
    moreDetails: null,
    userRoles: [],
    assets: [],
  });

  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const {
    fetchSuiteById,
    fetchSuiteCounts,
    fetchPropertySuites,
    fetchProperties,
    fetchAssetsBySuiteId,
    fetchSuiteWalkThroughs,
    fetchFaqsNotesDocuments,
    fetchPropertyManagerDetailsForHomeowner,
    fetchCalendlyLink,
    fetchAllowedTabList,
    getAllUserTypeRoles,
    updateSuite,
    deleteUnit,
    fetchMoreDetails,
    reRenderKeyPickupPdf,
    isLoading,
  }: UseSuiteActionsReturn = useSuiteActions();

  const { submitKeyHandoff, resetState }: UseKeyHandoffSubmissionReturn =
    useKeyHandoffSubmission();

  const suiteIdParts: string[] = useMemo(
    () => match.params.id.split('&'),
    [match.params.id]
  );

  const authenticatedUser: any = useSelector(selectAuthenticatedUser);
  const userRole: any = authenticatedUser.roles[0]?.role?.name;
  const user: any = authenticatedUser;

  /* Redux selectors (keeping these for compatibility with existing components)
   * These will need to be refactored away from Redux later as part of a deeper refactor */
  const suiteFormData = useSelector(selectSuiteFormData);
  const propertyWalkThroughFormIsVisible = useSelector(
    selectPropertyWalkThroughFormIsVisible
  );

  // Redux action dispatchers.
  const onSetSuiteFormData = useCallback(
    (data: any) => dispatch(setSuiteFormData(data)),
    [dispatch]
  );

  const onClearSuiteFormData = useCallback(
    () => dispatch(clearSuiteFormData()),
    [dispatch]
  );

  const serviceRequestId = location.state?.data?.serviceRequestId || null;
  const { orders } = store.getState().onboardingData;
  const showHomeownerDashboardNavigation = Object.keys(orders).length > 0;

  const currentTheme = localStorage.getItem('theme');
  const currentDay = serviceRequestTableLocalTime(moment(), 'suitepage');

  const isHomeOwnerOccupantUser = (user: any): boolean => {
    return !!(
      user?.roles?.length > 0 &&
      ['Home Owner', 'Realtor', 'Occupant'].includes(user.roles[0].role.name)
    );
  };

  const isHomeOwnerCheckUser = (user: any): boolean => {
    return !!(
      user?.roles?.length > 0 &&
      ['Home Owner', 'Realtor', 'Occupant', 'Strata Manager'].includes(
        user.roles[0].role.name
      )
    );
  };

  const calculateWarrantyDaysRemaining = (
    warrantyDate: string,
    currentMoment: any
  ): number => {
    const estimatedDaysRemaining: number = moment(warrantyDate).diff(
      currentMoment,
      'days'
    );
    return estimatedDaysRemaining - 1;
  };

  const fetchSuiteData: () => Promise<void> =
    useCallback(async (): Promise<void> => {
      try {
        setError(null);

        const suiteId: string = suiteIdParts[1];
        const propertyId: string =
          suiteIdParts.length === 3 ? suiteIdParts[2] : suiteIdParts[0];

        const suite: SuiteType | null = await fetchSuiteById(suiteId);
        const counts: Counts | null = await fetchSuiteCounts(suiteId, userRole);
        const properties: Property[] = await fetchProperties(user.id, userRole);
        const property: Property | undefined = properties.find(
          (p: Property) =>
            p.id ===
            (suiteIdParts.length === 3 ? suiteIdParts[2] : suiteIdParts[0])
        );
        const walkthroughs: WalkThrough[] = await fetchSuiteWalkThroughs(
          suiteId,
          userRole,
          true
        );
        const userRoles: UserRole[] = await getAllUserTypeRoles();
        const assets: Asset[] = await fetchAssetsBySuiteId(
          suiteId,
          user.id,
          userRole
        );
        const allowedTabList: ModulePermission[] = await fetchAllowedTabList(
          location.state?.property?.id,
          branding
        );

        let documents: NoteDocument[] = [];

        if (location.state?.suite?.id) {
          documents = await fetchFaqsNotesDocuments(location.state.suite.id);
        }

        let propertyManagerDetails: Contact | null = null;

        if (
          user?.roles?.length > 0 &&
          ['Home Owner', 'Occupant'].includes(user.roles[0].role.name)
        ) {
          propertyManagerDetails =
            await fetchPropertyManagerDetailsForHomeowner(propertyId);

          if (propertyManagerDetails?.email) {
            const calendlyData: CalendlyData = await fetchCalendlyLink(
              propertyManagerDetails.email
            );

            if (calendlyData) {
              propertyManagerDetails = {
                ...propertyManagerDetails,
                ...calendlyData,
              };
            }
          }
        }

        setState(
          (prev: SuiteState): SuiteState => ({
            ...prev,
            suite: suite || null,
            counts: counts || null,
            walkThroughs: walkthroughs || [],
            properties: properties || [],
            property: property || null,
            propertyManagerDetails,
            allowedTabList: allowedTabList || [],
            notesList: documents || [],
            userRoles: userRoles || [],
            assets: assets || [],
          })
        );
      } catch (error: any) {
        console.error('❌ Error fetching suite data:', error);
        setError(error.message || 'Failed to load suite data');
        toast.error(`🚫 ${error.message || 'Failed to load suite data'}`);
      }
    }, [
      suiteIdParts,
      userRole,
      user.id,
      user.roles,
      location.state?.property?.id,
      location.state?.suite?.id,
      branding,
      fetchSuiteById,
      fetchSuiteCounts,
      fetchSuiteWalkThroughs,
      fetchProperties,
      fetchPropertySuites,
      getAllUserTypeRoles,
      fetchAssetsBySuiteId,
      fetchAllowedTabList,
      fetchFaqsNotesDocuments,
      fetchPropertyManagerDetailsForHomeowner,
      fetchCalendlyLink,
      setError,
    ]);

  // Handle additional service request data if needed.
  const fetchServiceRequestData = async (): Promise<void> => {
    if (!serviceRequestId) return;

    try {
      const propertyId = suiteIdParts[2];

      if (propertyId) {
        await fetchPropertySuites(propertyId, user.id, userRole);
      }
    } catch (error: any) {
      console.warn('Failed to fetch service request data:', error);
    }
  };

  // Handle homeowner dashboard feedback.
  const handleHomeownerFeedback = (): void => {
    if (user?.roles?.length > 0 && user.roles[0].role.name === 'Home Owner') {
      const isShowHomeownerDashboardPage = JSON.parse(
        localStorage.getItem('showHomeownerDasboardPage') || 'false'
      );

      if (!isShowHomeownerDashboardPage) {
        const isFeedbackReportPopup = localStorage.getItem(
          'IsHomerNavigatFromFeedBackTemplate'
        );
        if (isFeedbackReportPopup) {
          setState(
            (prev: SuiteState): SuiteState => ({
              ...prev,
              feedBackReportPopup: true,
            })
          );
        }
      }
    }
  };

  // Handle Calendly event state.
  const handleCalendlyState = (): void => {
    const currentUrl = window.location.href;
    const isCalendlyEventShown = currentUrl.includes('openCalendly');
    setState(
      (prev: SuiteState): SuiteState => ({
        ...prev,
        isEventShow: isCalendlyEventShown,
      })
    );
  };

  // Handle suite save.
  const onSaveSuite = async (
    suiteData: any,
    suiteAreas: any,
    floorPlanImage: any
  ): Promise<void> => {
    try {
      // Image handling
      let imageUrl = null;
      let isNewImage = false;

      if (floorPlanImage === suiteData.floorPlanURL) {
        imageUrl = suiteData.floorPlanURL;
      } else {
        if (suiteData.floorPlanURL?.length > 0) {
          isNewImage = true;
          imageUrl = await upload(suiteData.floorPlanURL[0]);
        }
      }

      // Parking and storage processing.
      const parkingStallsArray: any[] =
        suiteData.parkingStalls?.map((parkingStall: any) => ({
          Name: parkingStall.label,
          Type: 'ParkingStalls',
        })) || [];

      const storageLockerArray: any[] =
        suiteData.storageLockers?.map((storageLocker: any) => ({
          Name: storageLocker.label,
          Type: 'StorageLockers',
        })) || [];

      // Suite update parameters.
      if (suiteData.id) {
        suiteAreas.forEach((areaItem: any) => {
          if (areaItem.type === 'Other') {
            delete areaItem.id;
          }
        });

        const updateParameters = {
          id: suiteData.id,
          statusId: suiteData.statusId,
          propertyId: suiteData.propertyId,
          number: suiteData.number.trim(),
          stratalot: suiteData.stratalot ? suiteData.stratalot.trim() : '',
          Userid: user.id,
          Areas: suiteAreas,
          floorPlanURL: imageUrl,
          assetWarrantyDefaultStartDate: suiteData.assetWarrantyDefaultStartDate
            ? new Date(
                moment(suiteData.assetWarrantyDefaultStartDate).format('l LT')
              )
            : null,
          warrantyNumber: suiteData.warrantyNumber,
          warrantyAddress: suiteData.warrantyAddress,
          Building: suiteData.Building,
          BuildingId: suiteData.BuildingId || null,
          Level: suiteData.Level,
          LevelId: suiteData.LevelId || null,
          ParkingStalls: parkingStallsArray,
          StorageLockers: storageLockerArray,
          newImage: isNewImage,
          KeyFobs: suiteData.keyFobs,
          NumberOfKeys:
            suiteData.numberOfKeys === null ||
            suiteData.numberOfKeys === undefined
              ? 0
              : Number(suiteData.numberOfKeys),
          KeyHandOffInstructions: suiteData.keyHandOffInstructions,
          suiteKeysAndValues: suiteData.suiteKeysAndValues,
        };

        // Update suite and refresh data.
        await updateSuite(updateParameters);

        // Refresh suite data after update.
        const updatedSuite: SuiteType | null = await fetchSuiteById(
          suiteData.id
        );
        const updatedCounts: Counts | null = await fetchSuiteCounts(
          suiteData.id,
          user.roles[0].role.name
        );
        await fetchAssetsBySuiteId(
          suiteData.id,
          user.id,
          user.roles[0].role.name
        );

        setState(
          (prev: SuiteState): SuiteState => ({
            ...prev,
            suite: updatedSuite,
            counts: updatedCounts,
          })
        );
      }
    } catch (error: any) {
      console.error('Failed to save suite:', error);
      toast.error(`🚫 ${error.message || 'Failed to save suite'}`);
    }
  };

  // Handle unit deletion.
  const handleDeleteUnit = async (unitId: string): Promise<void> => {
    try {
      const propertyFromState = location.state?.property;
      await deleteUnit(unitId, user.id, history, propertyFromState);
    } catch (error: any) {
      console.error('Failed to delete unit:', error);
    }
  };

  // Handle more details fetch.
  const handleFetchMoreDetails = async (): Promise<void> => {
    try {
      const details = await fetchMoreDetails(state.suite?.id);
      setState(
        (prev: SuiteState): SuiteState => ({
          ...prev,
          moreDetails: details,
        })
      );
    } catch (error: any) {
      console.error('Failed to fetch more details:', error);
    }
  };

  // Handle key pickup PDF re-render.
  const handleReRenderKeyPickupPdf = async (): Promise<void> => {
    try {
      await reRenderKeyPickupPdf(state.suite?.id);
    } catch (error: any) {
      console.error('Failed to re-render key pickup PDF:', error);
    }
  };

  // Mobile sync toggle.
  const toggleMobileSync = (): void => {
    if (!state.suite) return;

    const updatedSuite: SuiteType = {
      ...state.suite,
      mobileSyncAllowed: !state.suite.mobileSyncAllowed,
    };
    setState(
      (prev: SuiteState): SuiteState => ({
        ...prev,
        suite: updatedSuite,
      })
    );
  };

  // Handle fetch details for key handoff.
  const handleFetchDetails = (): void => {
    const { suite } = state;
    if (!suite) return;

    onSetSuiteFormData(suite);
    onSetSuiteFormData({ old: true });
    setState(
      (prev: SuiteState): SuiteState => ({
        ...prev,
        showOverlayKeyHandoff: true,
      })
    );
  };

  useEffect(() => {
    const initializeComponent = async (): Promise<void> => {
      try {
        setIsInitialLoading(true);
        setError(null);

        handleCalendlyState();
        handleHomeownerFeedback();

        await fetchSuiteData();
        await fetchServiceRequestData();
      } catch (error: any) {
        console.error('Component initialization failed:', error);
        setError(error.message || 'Failed to initialize component');
      } finally {
        setIsInitialLoading(false);
      }
    };

    initializeComponent();

    return () => {
      onClearSuiteFormData();
    };
  }, []);

  const handleCloseKeyPickupDialogue = async () => {
    setState(
      (prev: SuiteState): SuiteState => ({
        ...prev,
        showOverlayKeyHandoff: false,
      })
    );
    onClearSuiteFormData();
    await fetchSuiteData();
    resetState();
  };

  const {
    suite,
    counts,
    walkThroughs,
    properties,
    property,
    showOverlay,
    showOverlayKeyHandoff,
    feedBackReportPopup,
    allowedTabList,
    notesList,
    moreDetails,
  }: SuiteState = state;

  console.log('property: ', property);

  const noteVisibleToHomeownerOccupantRealtor: boolean =
    useMemo((): boolean => {
      if (!notesList?.length) return false;

      const notesArray: NoteDocument[] = notesList.filter(
        (noteItem: NoteDocument) => noteItem.type === 'NOTES'
      );
      if (!notesArray.length) return false;

      const homeOwnerOccupantRealtorNotes: NoteDocument[] = notesArray.map(
        (noteObject: NoteDocument) =>
          JSON.parse(JSON.parse(JSON.stringify(noteObject.data)))
      );

      const visibleNotes: NoteDocument[] = homeOwnerOccupantRealtorNotes.filter(
        (noteItem: any) =>
          noteItem.noteVisibleToHomeownerOccupantRealtor === true
      );

      return visibleNotes.length > 0;
    }, [notesList]);

  const oneYearWarrantyDaysRemaining: number = useMemo((): number => {
    if (!suite?.assetWarrantyOneYear) return 0;

    return calculateWarrantyDaysRemaining(
      suite.assetWarrantyOneYear,
      currentDay
    );
  }, [suite?.assetWarrantyOneYear, currentDay]);

  const twoYearWarrantyDaysRemaining: number = useMemo((): number => {
    if (!suite?.assetWarrantyTwoYear) return 0;

    return calculateWarrantyDaysRemaining(
      suite.assetWarrantyTwoYear,
      currentDay
    );
  }, [suite?.assetWarrantyTwoYear, currentDay]);

  const isHomeOwnerCheckUserResult: boolean = useMemo(
    () => isHomeOwnerCheckUser(user),
    [user]
  );
  const isHomeOwnerOccupantUserResult: boolean = useMemo(
    () => isHomeOwnerOccupantUser(user),
    [user]
  );

  const activeTabIndex: number = useMemo((): number => {
    const currentLocation: any = location;

    if (currentLocation?.state?.activeTab !== undefined) {
      const hasActiveWalkThrough: boolean = walkThroughs.some(
        (walkThroughItem: WalkThrough) => {
          if (walkThroughItem.walkthroughType === 'Home Owner Walk-through') {
            return [
              'Guided Walk Through',
              'Self-Guided Walk Through',
              'In Progress Home Only',
              'In Progress Home',
              'In Progress',
              'Begin',
              'Schedule',
            ].includes(walkThroughItem.status);
          }
          return false;
        }
      );

      return hasActiveWalkThrough && walkThroughs.length > 0
        ? currentLocation.state.activeTab + 1
        : currentLocation.state.activeTab;
    }

    const currentUrlString: string = window.location.href;
    const urlActiveTab: string = currentUrlString.split('activeTab=')[1];
    return urlActiveTab ? Number(urlActiveTab) : 0;
  }, [location, walkThroughs]);

  // Navigation state checking.
  const isFromDashboard: boolean = useMemo(
    () => location?.state?.from === 'Dashboard',
    [location]
  );

  // Link view permissions.
  const hasLinkViewPermission: boolean = useMemo((): boolean => {
    const adminRoles = [
      'Admin',
      'Super Admin',
      'Property Developer',
      'Strata Manager',
    ];

    return adminRoles.includes(user.roles[0]?.role?.name);
  }, [user]);

  // Parking details check.
  const parkingDetailsTab: ModulePermission = useMemo(
    () =>
      allowedTabList.find(
        (tabItem: ModulePermission) => tabItem.name === 'Parking Details'
      ),
    [allowedTabList]
  );

  // Determine which components to show.
  const isHomeOwnerRealtorUser: boolean = useMemo(
    (): boolean =>
      !parkingDetailsTab &&
      ['Home Owner', 'Realtor'].includes(user.roles[0]?.role?.name),
    [parkingDetailsTab, user]
  );

  // Memoized suite details components.
  const suiteDetailsComponents: JSX.Element[] = useMemo(
    () =>
      suite
        ? SuiteDetailsComponents({
            suite,
            counts,
            currentTheme,
            oneYearWarrantyDaysRemaining,
            twoYearWarrantyDaysRemaining,
            isHomeOwnerUser: isHomeOwnerRealtorUser,
            hasLinkViewPermission,
            isFromDashboard,
            feedBackReportPopup,
            onFetchMoreDetails: handleFetchMoreDetails,
          })
        : [],
    [
      suite,
      counts,
      currentTheme,
      oneYearWarrantyDaysRemaining,
      twoYearWarrantyDaysRemaining,
      isHomeOwnerRealtorUser,
      hasLinkViewPermission,
      isFromDashboard,
      feedBackReportPopup,
      handleFetchMoreDetails,
    ]
  );

  if (isInitialLoading || !suite) {
    return <Loader center={true} />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">
            Error Loading Suite
          </h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <WithNavigation
        title={suite.number}
        top={
          <HeadingWithDetails
            style={{
              backgroundImage: 'url(/assets/banner-property.png)',
            }}
            breadcrumb={
              <SuiteBreadcrumb
                serviceRequestId={serviceRequestId}
                isFromDashboard={isFromDashboard}
                userRole={user?.roles[0].role?.name}
                currentLocation={location}
                showHomeownerDashboardNavigation={
                  showHomeownerDashboardNavigation
                }
                suiteIdParts={suiteIdParts}
                property={property}
              />
            }
            headline={
              <Headline
                suiteName={suite?.number || ''}
                showProfile={suite?.primaryContact}
                name={
                  suite?.primaryContact?.firstname +
                  ' ' +
                  suite?.primaryContact?.lastname
                }
                disableLink={!suite?.primaryContact?.id}
                profileLinkTo={{
                  pathname: PATHS.CONTACTS_VIEW.replace(
                    ':id',
                    suite?.primaryContact?.id
                  ),
                  state: { isFromDashBoard: isFromDashboard },
                }}
                phone={suite?.primaryContact?.phone}
                image={undefined}
              />
            }
            items={suiteDetailsComponents}
            actions={
              <SuiteActions
                isHomeOwnerCheckUser={isHomeOwnerCheckUserResult}
                suite={suite}
                suiteFormData={suiteFormData}
                user={user}
                onToggleMobileSync={toggleMobileSync}
                onReRenderKeyPickupPdf={handleReRenderKeyPickupPdf}
                onFetchDetails={handleFetchDetails}
                onEditSuite={() => {
                  setState(
                    (prev: SuiteState): SuiteState => ({
                      ...prev,
                      showOverlay: true,
                    })
                  );
                  onSetSuiteFormData(suite); // Todo: We should stop punting this via Redux.
                }}
                onDeleteUnit={handleDeleteUnit}
                showOverlay={showOverlay}
                onCloseOverlay={() =>
                  setState(
                    (prev: SuiteState): SuiteState => ({
                      ...prev,
                      showOverlay: false,
                    })
                  )
                }
                onSaveSuite={onSaveSuite}
              />
            }
          />
        }
        bottom={
          <>
            <SuiteTabs
              walkThroughs={walkThroughs}
              userRole={user.roles[0].role.name}
              activeTabIndex={activeTabIndex}
              isHomeOwnerOccupantUser={isHomeOwnerOccupantUserResult}
              suite={suite}
              property={property}
              propertyManagerDetails={state.propertyManagerDetails}
              history={history}
              counts={counts}
              branding={branding}
              match={match}
              isEventShow={state.isEventShow}
              noteVisibleToHomeownerOccupantRealtor={
                noteVisibleToHomeownerOccupantRealtor
              }
              onFetchSuiteData={() => fetchSuiteData()}
              properties={properties}
              allowedTabList={allowedTabList}
            />
            <Overlay
              showOverlay={propertyWalkThroughFormIsVisible}
              content={
                <PropertyWalkThroughForm onClose={() => console.log('')} />
              }
            />
            <Overlay
              showOverlay={showOverlayKeyHandoff}
              content={
                <KeyHandoffComponent
                  submitKeyHandoff={submitKeyHandoff}
                  onClose={() => {
                    setState(
                      (prev: SuiteState): SuiteState => ({
                        ...prev,
                        showOverlayKeyHandoff: false,
                      })
                    );
                    onClearSuiteFormData();
                  }}
                  closeKeyPickupDialogueAndRefreshContent={
                    handleCloseKeyPickupDialogue
                  }
                  suiteFormData={suite}
                />
              }
            />
            {isLoading && <Loader center={true} />}
            {moreDetails && (
              <MoreDetails
                closePopup={() =>
                  setState(
                    (prev: SuiteState): SuiteState => ({
                      ...prev,
                      moreDetails: null,
                    })
                  )
                }
                details={moreDetails}
              />
            )}
          </>
        }
      />
    </div>
  );
};

export default Suite;
